import { ChangeDetectionStrategy, Component, Input, OnInit, Self } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { from, Observable, of } from 'rxjs';

import { EASButtonAction, EASLinkAction } from '@fry/components/common/buttons/buttons.service';
import { BookingItemsStore } from '@fry/booking/lib/booking-items';
import { BookingOptionParts } from '@fry/booking/lib/booking-items/booking-items.constants';
import { BookingsStore, UserBookingsStore } from '@fry/booking/lib/bookings';
import { NotifyService } from '@fry/lib/utils';
import { BookingOptionDoc } from '@fry/booking/lib/booking-items/booking-option';
import { switchMap } from 'rxjs/operators';
import { TrackOperationProgress, TracksOperationProgress } from '@fry/components/common';
import { RemoteUserService } from '@fry/lib/users';

import {
  DialogComponentData,
  DialogComponentService,
  DialogComponentStateFactory
} from '@fry/components/common/dialog';
import { SecurityService } from '@fry/lib/security';

const MODAL_DIALOG_STATES: {[K: string]: DialogComponentStateFactory} = {
  'canceled':
    () => DialogComponentData.canceled(),

  'error':
    (content) => DialogComponentData.error($localize `Submission error`, content),

  'progress':
    () => DialogComponentData.progress($localize `Processing`,
                                       $localize `This may take a little while depending on your connection`),

  'success':
    () => DialogComponentData.success()
};

@Component({
    selector: 'eas-booking-item-option-slot',
    templateUrl: './booking-item-option-slot.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        DialogComponentService
    ],
    standalone: false
})
export class BookingItemOptionSlotComponent implements OnInit, TracksOperationProgress {
  @Input() bookingItemOption: BookingOptionDoc;
  @Input() set options(options: {showCapacityInfo?: boolean}) {
    this._options = options || {};
  }

  get options() {
    return this._options;
  }

  private _options: {showCapacityInfo?: boolean} = {};

  public buttons: (EASButtonAction | EASLinkAction)[];
  public availableParts = BookingOptionParts.filter(itm => itm.id !== 'eligibility' && itm.id !== 'form' );
  public eligibilityDescription$;

  public capacityInfo$: Observable<any>;

  public isOperationInProgress = false;

  constructor(
      private bookingItems: BookingItemsStore,
      private bookings: UserBookingsStore,
      private allbookings: BookingsStore,
      private router: Router,
      private notify: NotifyService,
      private route: ActivatedRoute,
      private remoteUser: RemoteUserService,
      private security: SecurityService,
      @Self() private dialogComponentService: DialogComponentService,
  ) {
    this.dialogComponentService.states = MODAL_DIALOG_STATES;
  }

  ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      const parameter = params.get('path') as string;
      const path = parameter ? parameter.split(',') : [];

      this.buttons = [
        {
          type: 'button',
          label: $localize `Apply`,
          color: 'primary',
          condition: () => {
            if (!this.bookingItemOption.eligibilityResult) {
              return of(false);
            }

            return of(this.bookingItemOption.bookingAllowed);
          },
          click: () => {
            this.dialogComponentService.updateState(
              this.dialogComponentService.stateFor('progress')
            );
            this.onApplyForBookingOption$(this.bookingItemOption.bookingItem._id,
                                          this.bookingItemOption.id)
                .subscribe(
                  () => {
                    this.dialogComponentService.updateState(
                      this.dialogComponentService.stateFor('success')
                    );
                  },
                  (error) => {
                    this.dialogComponentService.updateState(
                      this.dialogComponentService.states['error'](error)
                    );

                    this.notify.error(error);
                  }
                );
          }
        },
        {
          type: 'button',
          label: $localize `Force apply`,
          color: 'primary',
          condition: () => {
            if (!this.bookingItemOption.eligibilityResult) {
              return of(false);
            }

            if (this.bookingItemOption.bookingAllowed) {
              return of(false);
            }

            if (this.remoteUser.user) {
              return this.security.hasPermissionFor(
                'bookings.manage',
                this.remoteUser.user.doc.user,
                false,
                this.bookingItemOption.bookingItem._id
              );
            }
            return of(false);
          },
          click: () => {
            this.dialogComponentService.updateState(
              this.dialogComponentService.stateFor('progress')
            );
            this.onApplyForBookingOption$(this.bookingItemOption.bookingItem._id,
                                          this.bookingItemOption.id,
                                          { ignoreEligibility: true })
                .subscribe(
                  () => {
                    this.dialogComponentService.updateState(
                      this.dialogComponentService.stateFor('success')
                    );
                  },
                  (error) => {
                    this.dialogComponentService.updateState(
                      this.dialogComponentService.states['error'](error)
                    );

                    this.notify.error(error);
                  }
                );
          }
        },
        {
          type: 'button',
          label: $localize `View options`,
          color: 'primary',
          condition: () => of(!this.bookingItemOption.eligibilityResult && !this.options.showCapacityInfo),
          click: () => {
            const value = [...path, this.bookingItemOption.id].join(',');
            this.router.navigate([],
                                 {queryParams: { 'path': value }});
          }
        },
        {
          type: 'link',
          label: $localize `View details`,
          color: 'primary',
          condition: () => of(this.options.showCapacityInfo),
          link: of([this.bookingItemOption.id])
        },
      //   {
      //     type: 'link',
      //     label: $localize `Edit option`,
      //     color: 'primary',
      //     condition: () => of(this.options.showCapacityInfo && this.bookingItemOption.id !== this.bookingItemOption.bookingItem._id),
      //     link: of(['../', 'edit', this.bookingItemOption.id])
      //   }
      ];

      if (this.bookingItemOption.eligibility) {
        try {
          const eligibility = JSON.parse(this.bookingItemOption.eligibility.rules);
          this.eligibilityDescription$ = this.bookingItems.describeEligibility(eligibility);
        } catch (err) {
          this.eligibilityDescription$ = of($localize `Could not parse rules: ${err}`);
        }
      } else {
        this.eligibilityDescription$ = of($localize `No rules`);
      }

      if (this.bookingItemOption.bookableType === 'bookable' &&
          this.bookingItemOption.capacities &&
          this.bookingItemOption.capacities.availableCapacity > -1) {
        this.capacityInfo$ = this.bookingItems.getCapacityInfo(this.bookingItemOption.id, this.bookingItemOption.bookingItem._id);
      }

    });
  }

  //
  // Actions
  //

  @TrackOperationProgress()
  private onApplyForBookingOption$(bookingItem: string, bookingOption: string, options?: any) {
    const apply$ = this.remoteUser.user ?
      this.allbookings.apply(this.remoteUser.user.doc.user, bookingItem, bookingOption, options) :
      this.bookings.apply(bookingItem, bookingOption, options);

    return apply$
        .pipe(
          switchMap(result => {
            return from(this.router.navigate(['bookings',
                                              result.result.id,
                                              'edit']));
          })
        );
    }
}
